import { useState, useCallback, useRef, useEffect } from 'react';

/**
 * Custom hook for managing data refresh operations across all modules
 * Provides reliable data reload functionality after CRUD operations
 */
export const useDataRefresh = () => {
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const refreshTimeoutRef = useRef(null);
  const lastRefreshRef = useRef(0);

  // Force a refresh by incrementing the trigger
  const triggerRefresh = useCallback((delay = 100) => {
    const now = Date.now();
    // Prevent rapid successive refreshes
    if (now - lastRefreshRef?.current < 50) {
      return;
    }

    // Clear any existing timeout to prevent multiple refreshes
    if (refreshTimeoutRef?.current) {
      clearTimeout(refreshTimeoutRef?.current);
    }

    // Set refreshing state
    setIsRefreshing(true);

    // Trigger refresh with a small delay to ensure database consistency
    refreshTimeoutRef.current = setTimeout(() => {
      setRefreshTrigger(prev => prev + 1);
      lastRefreshRef.current = Date.now();
      setIsRefreshing(false);
    }, delay);
  }, []);

  // Force immediate refresh (for critical updates like delete operations)
  const forceRefresh = useCallback(() => {
    const now = Date.now();
    
    // Clear any pending timeout
    if (refreshTimeoutRef?.current) {
      clearTimeout(refreshTimeoutRef?.current);
    }
    
    setIsRefreshing(true);
    setRefreshTrigger(prev => prev + 1);
    lastRefreshRef.current = now;
    
    // Reset refreshing state quickly for delete operations
    setTimeout(() => setIsRefreshing(false), 100);
  }, []);

  // Batch multiple refresh requests
  const debouncedRefresh = useCallback((delay = 300) => {
    if (refreshTimeoutRef?.current) {
      clearTimeout(refreshTimeoutRef?.current);
    }

    refreshTimeoutRef.current = setTimeout(() => {
      triggerRefresh(0);
    }, delay);
  }, [triggerRefresh]);

  // Cleanup timeout on unmount
  const cleanup = useCallback(() => {
    if (refreshTimeoutRef?.current) {
      clearTimeout(refreshTimeoutRef?.current);
    }
  }, []);

  return {
    refreshTrigger,
    isRefreshing,
    triggerRefresh,
    forceRefresh,
    debouncedRefresh,
    cleanup
  };
};

/**
 * Enhanced data loading hook that integrates with refresh manager
 */
export const useDataLoader = (loadFunction, dependencies = [], options = {}) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const loadingRef = useRef(false);
  const mountedRef = useRef(true);

  const {
    retryAttempts = 3,
    retryDelay = 1000,
    optimisticUpdate = false
  } = options;

  // Set mounted ref on mount/unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const executeLoad = useCallback(async (attempt = 1) => {
    // Don't execute if component is unmounted
    if (!mountedRef?.current) return;
    
    // Prevent multiple simultaneous loads
    if (loadingRef?.current) {
      return;
    }

    try {
      loadingRef.current = true;
      setLoading(true);
      setError(null);

      const result = await loadFunction();

      // Check if component is still mounted before setting state
      if (!mountedRef?.current) return;

      if (result?.error) {
        throw new Error(result.error.message || 'Failed to load data');
      }

      setData(result?.data || []);
    } catch (error) {
      if (!mountedRef?.current) return;
      
      console.error('Data loading error:', error);

      // Retry logic
      if (attempt < retryAttempts) {
        console.log(`Retrying data load (attempt ${attempt + 1}/${retryAttempts})`);
        setTimeout(() => {
          if (mountedRef?.current) {
            executeLoad(attempt + 1);
          }
        }, retryDelay * attempt);
        return;
      }

      setError(error?.message || 'Failed to load data');
      setData([]);
    } finally {
      if (mountedRef?.current) {
        setLoading(false);
      }
      loadingRef.current = false;
    }
  }, [loadFunction, retryAttempts, retryDelay]);

  // Execute load when dependencies change
  useEffect(() => {
    executeLoad();
  }, dependencies);

  // Optimistic update for delete operations
  const optimisticRemove = useCallback((itemId) => {
    if (optimisticUpdate) {
      setData(prevData => prevData?.filter(item => item?.id !== itemId));
    }
  }, [optimisticUpdate]);

  // Optimistic update for create operations
  const optimisticAdd = useCallback((newItem) => {
    if (optimisticUpdate && newItem) {
      setData(prevData => [newItem, ...prevData]);
    }
  }, [optimisticUpdate]);

  return {
    data,
    loading,
    error,
    reload: executeLoad,
    setData: optimisticUpdate ? setData : undefined,
    optimisticRemove,
    optimisticAdd
  };
};

export default useDataRefresh;